import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    rRemoveIncident,
    rUpdateIncident
} from "services/redux/incidentForm/incidentForm";
import { AppDispatch, RootState } from "services/redux/store";
import {
    FormGifViewer,
    FormImageViewer,
    FormIncidentControls,
    FormVideoViewer,
    SVG,
    TextArea,
    customToaster
} from "ui";
import {
    CustomComponentProps,
    IncidentFormObject
} from "utils/types/generalTypes";

export type PostFormProps = CustomComponentProps & {
    incidentId: number;
    incident: IncidentFormObject;
};

const IncidentForm = ({
    incidentId,
    incident,
    className,
    style
}: PostFormProps) => {
    const mode = useSelector((state: RootState) => state.site.mode);
    const dispatch = useDispatch<AppDispatch>();
    const { avatar } = useSelector((state: RootState) => state.account);

    // ------------------------- validate all post on update any field
    const toggleValid = () => {
        if (
            !incident.text.length &&
            !incident.images &&
            !incident.video &&
            !incident.gif &&
            incident.isValid
        ) {
            dispatch(rUpdateIncident({ ...incident, isValid: false }));

            return;
        }

        // set post to be valid
        if (!incident.isValid) {
            dispatch(rUpdateIncident({ ...incident, isValid: true }));
            return;
        }
    };

    const handleRemoveMedia = () => {
        dispatch(
            rUpdateIncident({
                ...incident,
                images: undefined,
                mediaType: null,
                video: undefined,
                gif: undefined
            })
        );
    };

    useEffect(() => {
        toggleValid();
    }, [incident.text, incident.images, incident.video, incident.gif]);

    // ------------------------- validate text part
    const handleChangeText = (val: string) => {
        if (val.length > 250) {
            return;
        } else {
            dispatch(rUpdateIncident({ ...incident, text: val }));
        }
    };

    // ------------------------- validate image part
    const handleChangeImage = (images: FileList) => {
        // validate image
        if (!images[0]) {
            return;
        }
        if (images.length > 4) {
            customToaster("error", "You can select up to 4 images only", mode);
            return;
        }

        const imagesArr: File[] = [];
        let valid = true;
        // eslint-disable-next-line
        for (let image of Array.from(images)) {
            if (!image.name.match(/\.(jpg|jpeg|png)$/)) {
                valid = false;
                break;
            } else {
                imagesArr.push(image);
            }
        }
        if (!valid) {
            customToaster("error", "Please select valid imag", mode);
            return;
        }
        dispatch(
            rUpdateIncident({
                ...incident,
                images: imagesArr,
                mediaType: "img",
                video: undefined,
                gif: undefined
            })
        );
    };

    const removeImg = (img: File) => {
        if (!incident.images) {
            return;
        }

        const imagesArr = incident.images.filter((i) => i != img);

        if (imagesArr.length) {
            dispatch(
                rUpdateIncident({
                    ...incident,
                    images: imagesArr,
                    mediaType: "img",
                    video: undefined,
                    gif: undefined
                })
            );
        } else {
            dispatch(
                rUpdateIncident({
                    ...incident,
                    images: undefined,
                    mediaType: null,
                    video: undefined,
                    gif: undefined
                })
            );
        }
        // toggleValid();
    };

    // ------------------------- validate video part
    const handleChangeVideo = (videos: FileList) => {
        if (!videos[0]) {
            return;
        }
        if (videos.length > 1) {
            customToaster("error", "You can select 1 video only", mode);
            return;
        }

        if (!videos[0].name.match(/\.(mp4|mkv|webm|3gp)$/)) {
            customToaster("error", "Please select valid Video", mode);
        } else {
            dispatch(
                rUpdateIncident({
                    ...incident,
                    video: videos[0],
                    mediaType: "vid",
                    images: undefined,
                    gif: undefined
                })
            );
        }
    };

    // ------------------------- handle remove
    const handleRemoveIncident = () => {
        if (incidentId == 1) {
            return;
        }
        dispatch(rRemoveIncident(incidentId));
    };

    // incident location
    const incidentLocation = useSelector(
        (state: RootState) => state.incidentForm.incidentLocation
    );

    return (
        <div
            className={`post-form d-flex flex-j-start gap-10 flex-a-start p-relative ${className} `}
            style={style}
        >
            <div className="post-form-v-line"></div>
            <div className="post-form-avatar p-relative">
                <div className="img-col navbar-item is-account">
                    <a href="#" className="d-flex flex-a-center">
                        <img src={avatar} alt="profile picture" />
                    </a>
                </div>
            </div>

            <div className="post-form-viewer">
                <TextArea
                    text={incident.text}
                    handleChangeText={handleChangeText}
                />

                {/* media viewer part  */}
                <div className="media-displayer pt-10 pb-10">
                    {/* selected image viewer   */}
                    {incident.images ? (
                        <FormImageViewer
                            images={incident.images}
                            removeImg={removeImg}
                        />
                    ) : (
                        ""
                    )}

                    {/* selected video viewer */}
                    {incident.video ? (
                        <FormVideoViewer
                            video={incident.video}
                            handleRemoveMedia={handleRemoveMedia}
                        />
                    ) : (
                        ""
                    )}

                    {/* gif viewer  */}
                    {incident.gif ? (
                        <FormGifViewer
                            gif={incident.gif}
                            handleRemoveMedia={handleRemoveMedia}
                        />
                    ) : (
                        ""
                    )}
                </div>

                <div className="momeento-p d-flex flex-j-start gap-5 pb-10">
                    <SVG svg="map-pointer" size={18} className="red-svg" />
                    <span>{incidentLocation.address}</span>
                </div>

                {/* media import btns  */}
                <FormIncidentControls
                    handleChangeImage={handleChangeImage}
                    handleChangeVideo={handleChangeVideo}
                    incidentId={incidentId}
                    handleRemoveIncident={handleRemoveIncident}
                />
            </div>
        </div>
    );
};

export default IncidentForm;
